import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatIDR } from '../../lib/format';
import AdminLayout from '../../components/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Users, UserCheck, UserX, Clock, CreditCard, Wallet, Coins, GitBranch, GraduationCap, Award, ShieldAlert, PlayCircle, Settings, Banknote } from 'lucide-react';

const RANGES = [['all','Semua'],['today','Hari Ini'],['7days','7 Hari'],['30days','30 Hari'],['month','Bulan Ini'],['year','Tahun Ini']];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [range, setRange] = useState('all');
  const load = (r) => api.get(`/admin/overview?range=${r}`).then(x => setD(x.data)).catch(()=>{});
  useEffect(() => { load(range); }, [range]);

  if (!d) return <AdminLayout title="Dashboard"><div className="h-64 flex items-center justify-center text-muted-foreground">Memuat...</div></AdminLayout>;
  const M = d.membership, F = d.finance, A = d.academy, R = d.risk;

  const quick = [
    ['Review Payments', CreditCard, '/admin/payments'],['Review Commissions', GitBranch, '/admin/commissions'],
    ['Review Withdrawals', Banknote, '/admin/withdrawals'],['Run Monthly Jobs', PlayCircle, '/admin/jobs'],
    ['Business Settings', Settings, '/admin/settings'],['Members', Users, '/admin/members'],
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-sm text-muted-foreground">Admin Control Center — ringkasan operasional bisnis.</p>
        <Select value={range} onValueChange={setRange}><SelectTrigger className="w-40" data-testid="dashboard-range"><SelectValue /></SelectTrigger>
          <SelectContent>{RANGES.map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {quick.map(([label,Icon,to]) => (<Button key={label} variant="outline" size="sm" onClick={() => nav(to)} data-testid={`quick-${label.split(' ')[0].toLowerCase()}`}><Icon className="h-4 w-4 mr-1" />{label}</Button>))}
      </div>

      <Section title="Membership">
        <Stat icon={Users} label="Total Members" value={M.total_members} />
        <Stat icon={UserCheck} label="Active" value={M.active} good />
        <Stat icon={UserX} label="Expired" value={M.expired} />
        <Stat icon={UserX} label="Suspended" value={M.suspended} />
        <Stat icon={Clock} label="Expiring 7d" value={M.expiring_7d} warn />
        <Stat icon={Clock} label="Expiring 30d" value={M.expiring_30d} />
        <Stat icon={Users} label="New Today" value={M.new_today} />
        <Stat icon={Users} label="New This Month" value={M.new_month} />
      </Section>

      <Section title="Finance">
        <Stat icon={Wallet} label="Total Revenue" value={formatIDR(F.total_revenue)} />
        <Stat icon={Wallet} label="Revenue Bulan Ini" value={formatIDR(F.revenue_month)} />
        <Stat icon={CreditCard} label="Pending Payments" value={F.payments_pending} warn />
        <Stat icon={CreditCard} label="Verified Payments" value={F.payments_verified} />
        <Stat icon={Coins} label="Commission Total" value={formatIDR(F.commission_total)} />
        <Stat icon={Coins} label="Commission Unpaid" value={formatIDR(F.commission_unpaid)} warn />
        <Stat icon={Coins} label="Commission Paid" value={formatIDR(F.commission_paid)} good />
        <Stat icon={Coins} label="Total Allocation" value={formatIDR(F.total_allocation)} />
        <Stat icon={Banknote} label="Pending Withdrawal" value={F.withdrawal_pending} warn />
        <Stat icon={Banknote} label="Withdrawal Paid" value={formatIDR(F.withdrawal_paid)} />
      </Section>

      <Section title="Academy">
        <Stat icon={GraduationCap} label="Total Students" value={A.total_students} />
        <Stat icon={GraduationCap} label="Active Learners" value={A.active_learners} />
        <Stat icon={GraduationCap} label="Avg Progress" value={`${A.avg_progress}%`} />
        <Stat icon={GraduationCap} label="Avg Quiz" value={`${A.avg_quiz}%`} />
        <Stat icon={Award} label="Certificates" value={A.certificates} />
        <Stat icon={Award} label="Completion Rate" value={`${A.completion_rate}%`} />
      </Section>

      <Section title="Risk">
        <Stat icon={ShieldAlert} label="Open Fraud" value={R.open_cases} warn />
        <Stat icon={ShieldAlert} label="High Risk" value={R.high_risk} warn />
        <Stat icon={UserX} label="Suspended Users" value={R.suspended_users} />
        <Stat icon={GitBranch} label="Suspicious Referrals" value={R.suspicious_referrals} />
      </Section>

      <Card className="p-5 mt-6">
        <h3 className="font-semibold mb-4">Pendaftaran per Bulan</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={d.signups}><CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
            <Tooltip /><Bar dataKey="count" fill="hsl(43 74% 52%)" radius={[6,6,0,0]} /></BarChart>
        </ResponsiveContainer>
      </Card>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (<div className="mb-6"><h2 className="font-display font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">{title}</h2><div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">{children}</div></div>);
}
function Stat({ icon: Icon, label, value, good, warn }) {
  return (<Card className="p-4"><div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div><div className={`font-display text-xl font-bold mt-1.5 tabular-nums ${good?'text-emerald-600':warn?'text-amber-600':''}`}>{value}</div></Card>);
}
